import React, {useState} from 'react';
import {Dimensions, PixelRatio, View} from 'react-native';

import Checkbox from '/component/base/Checkbox';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {SupportedLanguagesTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {LANGUAGES} from '../ManageMySite/ManageMySite.constants';
import {BottomSheetModal} from '../ManageMySite/widgets/BottomSheetModal';

import {useSupportedLanguages} from './SupportedLanguagesPopover.hook';
import {styles} from './SupportedLanguagesPopover.style';
import {SupportedLanguagesPopoverProps} from './SupportedLanguagesPopover.types';

const TITLE_TEXT = String(tt('supportedLanguages', 'Supported Languages'));
const DEFAULT_INITIAL_LANGUAGES = ['en'];

export const SupportedLanguagesPopover = ({
  visible = true,
  onClose,
  onSave = async () => {},
  isSaving = false,
  initialLanguages = DEFAULT_INITIAL_LANGUAGES,
  languageOptions = LANGUAGES,
}: SupportedLanguagesPopoverProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {selected, isDirty, toggleLanguage, handleClearAll, resetState} =
    useSupportedLanguages(initialLanguages);

  const saving = isSaving || isSubmitting;

  const normalizedSelected = selected.map(item => String(item).toLowerCase());
  const isSpanishSelected = normalizedSelected.includes('es');

  const {width, height} = Dimensions.get('window');
  const isLandscape = width > height;

  const fontScale = PixelRatio.getFontScale();

  const scaledSelectedIconSize = fontScale > 1.3 ? 26 : 24;

  const scaledUnselectedIconSize = fontScale > 1.3 ? 26 : 24;

  const handleClose = (): void => {
    if (saving) {
      return;
    }

    if (isDirty) {
      resetState();
    }

    onClose?.();
  };

  const handleSave = async (): Promise<void> => {
    if (saving || !isDirty) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onSave(selected);

      setTimeout(() => {
        onClose?.();
      }, 300);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  return (
    <BottomSheetModal
      useNativeModal={false}
      visible={visible}
      title={TITLE_TEXT}
      onClose={handleClose}
      onClearAll={handleClearAll}
      onSave={handleSave}
      canSave={isDirty && !saving}
      isSaving={saving}
      scrollable
      isClearAllDisabled={!isSpanishSelected || saving}>
      <View
        accessible
        accessibilityLabel="Select the languages you can support for coaching sessions."
        testID={SupportedLanguagesTestIDs.description}>
        <Text
          style={styles.descriptionText}
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={SupportedLanguagesTestIDs.descriptionText}>
          {tt(
            'supportedLanguagesDescription',
            'Select the languages you can support for coaching sessions.',
          )}
        </Text>
      </View>

      <View
        style={styles.container}
        accessible={false}
        accessibilityRole="none"
        testID={SupportedLanguagesTestIDs.list}>
        {languageOptions.map(lang => {
          const languageValue = String(lang.value).toLowerCase();
          const isEnglish = languageValue === 'en';
          const isChecked = normalizedSelected.includes(languageValue);

          const checkboxLabel = isEnglish
            ? `${lang.label}, selected, required, disabled`
            : isChecked
            ? `${lang.label}, selected`
            : `${lang.label}, not selected`;

          const handleToggle = (): void => {
            if (saving || isEnglish) {
              return;
            }

            toggleLanguage(lang.value);
          };

          return (
            <TouchableOpacity
              key={lang.value}
              testID={SupportedLanguagesTestIDs.row(lang.value)}
              style={styles.checkRow}
              onPress={handleToggle}
              disabled={saving || isEnglish}
              accessible
              accessibilityRole="checkbox"
              accessibilityLabel={checkboxLabel}
              accessibilityState={{
                checked: isChecked,
                disabled: isEnglish || saving,
              }}
              accessibilityHint={
                isEnglish
                  ? 'Required language. Cannot be changed.'
                  : isChecked
                  ? `Double tap to deselect ${lang.label}`
                  : `Double tap to select ${lang.label}`
              }>
              <View
                style={styles.checkRowLeft}
                importantForAccessibility="no-hide-descendants">
                <View
                  accessible={false}
                  testID={SupportedLanguagesTestIDs.checkboxContainer(
                    lang.value,
                  )}>
                  <Checkbox
                    checked={isChecked}
                    rectangleCheckbox
                    accessible={false}
                    checkedIconName={
                      isChecked ? 'checkbox-selected' : 'checkbox-unselected'
                    }
                    readonly={!isEnglish && !saving}
                    disabled={isEnglish || saving}
                    onChange={handleToggle}
                    testID={SupportedLanguagesTestIDs.checkbox(lang.value)}
                    containerStyle={{
                      minWidth: 48,
                      minHeight: 48,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    style={{
                      icon: isEnglish
                        ? [
                            styles.selectedIcon,
                            styles.selectedIconDisabled,
                            {fontSize: scaledSelectedIconSize},
                          ]
                        : isChecked
                        ? [
                            styles.selectedIcon,
                            {fontSize: scaledSelectedIconSize},
                          ]
                        : [
                            styles.unSelectedIcon,
                            {
                              width: scaledUnselectedIconSize,
                              height: scaledUnselectedIconSize,
                            },
                          ],
                    }}
                  />
                </View>

                <View
                  testID={SupportedLanguagesTestIDs.languageText(lang.value)}>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.radioLabel}
                    testID={SupportedLanguagesTestIDs.languageLabel(
                      lang.value,
                    )}>
                    {lang.label}
                  </Text>
                </View>
              </View>

              {isEnglish && (
                <View
                  style={[
                    styles.requiredTextContainer,
                    isLandscape && styles.requiredTextContainerLandscape,
                  ]}
                  testID={SupportedLanguagesTestIDs.requiredTextContainer}>
                  <Text
                    style={styles.requiredText}
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    accessible={false}
                    importantForAccessibility="no"
                    testID={SupportedLanguagesTestIDs.requiredText}>
                    {tt('required', 'Required')}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    </BottomSheetModal>
  );
};
